import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { HiOutlineInformationCircle } from "react-icons/hi2";
import { HiCalculator } from "react-icons/hi";
import useAuth from "../../../../hooks/account/useAuth";
import CostCalculatorModal from "../../../components/modals/CostCalculatorModal";
import { CreateBusinessForm } from "../../../../types/forms";
import { BusinessSubscriptionType, SubscriptionType } from "../../../../types/misc";
import { businessFeaturesInSubscription, communitySubscriptionPrices, getMinimumBusinessSubscriptionForFeatures, isBusinessSubscriptionGreaterThanOrEqual } from "../../../../utils/BusinessFeatureUtils";
import { StepFlowStep } from "../../../components/misc/StepFlow";
import { SubscriptionSelector } from "../../../components/selection-lists/SubscriptionSelector";
import AreYouSureModal from "../../../components/modals/AreYouSureModal";
import businessService from "../../../../services/business/businessService";
import Button from "../../../components/buttons/Button";

type BusinessSubscriptionViewProps = {
  setStep: (step: 1 | -1) => void;
  business: CreateBusinessForm;
  setBusiness: (business: CreateBusinessForm) => void;
}

export default function BusinessSubscriptionView({ business, setBusiness, setStep }: BusinessSubscriptionViewProps) {
  const { t } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const [downgradeWarningOpen, setDowngradeWarningOpen] = useState(false);
  const [pendingSubscription, setPendingSubscription] = useState<BusinessSubscriptionType | null>(null);
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const formatPrice = (price: number) => price === 0 ? null : `€${price}`;

  const disableIncompatibleFeatures = () => {
    const selectedFeatures = { ...business.dashboardFeatures };
    const allowedFeatures = businessFeaturesInSubscription[pendingSubscription ?? BusinessSubscriptionType.STARTER];

    Object.keys(selectedFeatures).forEach((key) => {
      if (!allowedFeatures[key as keyof typeof allowedFeatures]) {
        selectedFeatures[key as keyof typeof selectedFeatures] = false;
      }
    });
    return selectedFeatures;
  };

  const handleSubscriptionSelection = (subscriptionType: BusinessSubscriptionType | SubscriptionType) => {
    const businessSubscription = subscriptionType as BusinessSubscriptionType;
    const requiredSubscription = getMinimumBusinessSubscriptionForFeatures(business.dashboardFeatures || {});

    const isValidSubscription = isBusinessSubscriptionGreaterThanOrEqual(businessSubscription, requiredSubscription);

    if (!isValidSubscription) {
      setPendingSubscription(businessSubscription);
      setDowngradeWarningOpen(true);
    } else {
      setBusiness({ ...business, subscriptionType: businessSubscription });
    }
  };

  const handleConfirmDowngrade = () => {
    if (pendingSubscription) {
      const updatedFeatures = disableIncompatibleFeatures();

      setBusiness({
        ...business,
        subscriptionType: pendingSubscription,
        dashboardFeatures: updatedFeatures,
        connectedMembers: false,
      });
    }

    setDowngradeWarningOpen(false);
    setPendingSubscription(null);
  };

  const handleCancelDowngrade = () => {
    setDowngradeWarningOpen(false);
    setPendingSubscription(null);
  };

  const handleSubmit = async () => {
    setLoading(true);
    await businessService.createBusiness(business)
    navigate('/overview');
    setLoading(false);
  };

  return (
    <>
      <StepFlowStep
        setStep={setStep}
        disabled={!business.subscriptionType || loading}
        showNext={!auth}
        onSubmit={auth ? handleSubmit : undefined}
        submitText={auth ? t('page.onboarding.confirmation.submit') : undefined}
        title={t('page.onboarding.business.subscription')}
        subtitle={t('page.onboarding.business.subscriptionDescription')}
        className="max-w-4xl">

        <Button
          variant="primary"
          onClick={() => setCalculatorOpen(true)}>
          <HiCalculator className="h-4 w-4" />
          {t('page.onboarding.subscription.calculator.open')}
        </Button>

        <SubscriptionSelector
          selectedSubscription={business.subscriptionType}
          mode="selectable"
          onSubscriptionSelect={handleSubscriptionSelection}
          showTrialPeriod
          trialDays={30}
          isBusiness
        />

        <div className="mt-4 rounded-xl bg-primary-100 p-4">
          <div className="flex items-start gap-3">
            <HiOutlineInformationCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary-900" />
            <div>
              <p className="text-sm font-semibold text-primary-900">
                {t('page.onboarding.subscription.subcommunityPayment.title')}
              </p>
              <p className="mt-1 text-sm text-primary-900 text-opacity-70">
                {t('page.onboarding.subscription.subcommunityPayment.description')}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 text-center">
          {Object.entries(communitySubscriptionPrices).map(([tier, price]) => (
            <div key={tier} className="rounded-xl bg-secondary-200 bg-opacity-50 p-4">
              <p className="text-sm font-medium text-primary-900 text-opacity-50">
                {t(`page.onboarding.subscription.${tier.toLowerCase()}.title`)}
              </p>
              <p className="mt-1 text-2xl font-bold text-primary-900">
                {formatPrice(price) ?? t('page.onboarding.subscription.pricing.free')}
              </p>
              <p className="text-xs text-primary-900 text-opacity-50">
                {t(`page.onboarding.subscription.pricing.${price === 0 ? 'perCommunity' : 'perCommunityMonth'}`)}
              </p>
            </div>
          ))}
        </div>
      </StepFlowStep>

      <CostCalculatorModal
        open={calculatorOpen}
        setOpen={setCalculatorOpen}
        initialSubscription={business.subscriptionType}
      />

      <AreYouSureModal
        open={downgradeWarningOpen}
        setOpen={setDowngradeWarningOpen}
        title={t('page.onboarding.subscription.downgrade.title')}
        description={t('page.onboarding.subscription.downgrade.description')}
        confirmLabel={t('page.onboarding.subscription.downgrade.confirm')}
        cancelLabel={t('page.onboarding.subscription.downgrade.cancel')}
        onConfirm={handleConfirmDowngrade}
        onCancel={handleCancelDowngrade}
      />
    </>
  );
}
